import { createContext, Dispatch, PropsWithChildren, useCallback, useContext, useReducer } from 'react';
import { OkDialog } from '../CommonControls/Dialog/OkDialog';
import { YesNoDialog } from '../CommonControls/Dialog/YesNoDialog';
import { Payload } from './Payload';
import css from './Message.module.scss';

interface ConfirmationMessage {
    /**
     * Message title
     */
    title: string;
    /**
     * Message text
     */
    message: string | JSX.Element;
    /**
     * Callback when message is clicked
     */
    onClose?: () => void;
}

export interface YesNoMessage {
    /**
     * Message title
     */
    title: string;
    /**
     * Message text
     */
    message: string | JSX.Element;
    /**
     * Callback when 'Yes' buton is clicked
     */
    onYes?: () => void;
    /**
     * Callback when 'No' buton is clicked
     */
    onNo?: () => void;
    /**
     * Label for 'Yes' button. Default 'Yes'
     */
    yesText?: string;
    /**
     * Label for 'No' button. Default 'No'
     */
    noText?: string;
}

export enum MessageDispatchTypes {
    SetConfirmation = 'SET_CONFIRMATION',
    SetYesNo = 'SET_YESNO',
    Clear = 'CLEAR'
}

type MessagePayload = {
    [MessageDispatchTypes.SetConfirmation]: ConfirmationMessage | undefined;
    [MessageDispatchTypes.SetYesNo]: YesNoMessage | undefined;
    [MessageDispatchTypes.Clear]: never;
};

type MessageActions = Payload<MessagePayload>[keyof Payload<MessagePayload>];

type MessageStateType = {
    ConfirmationMessage: ConfirmationMessage | undefined;
    YesNoMessage: YesNoMessage | undefined;
};

const initialState: MessageStateType = {
    ConfirmationMessage: undefined,
    YesNoMessage: undefined,
};

const messageReducer = (state: MessageStateType, action: MessageActions): MessageStateType => {
    switch (action.type) {
        case MessageDispatchTypes.SetConfirmation:
            return {
                ...state,
                ConfirmationMessage: action.payload,
            };
        case MessageDispatchTypes.SetYesNo:
            return {
                ...state,
                YesNoMessage: action.payload,
            };
        case MessageDispatchTypes.Clear:
            return {
                ...state,
                ConfirmationMessage: undefined,
                YesNoMessage: undefined,
            };
        default:
            return state;
    }
};

const MessageStateContext = createContext<MessageStateType>(initialState);
const MessageDispatchContext = createContext<Dispatch<MessageActions>>(() => null);

export const MessageProvider: React.FC<PropsWithChildren> = (props) => {
    const [state, dispatch] = useReducer(messageReducer, initialState);

    return (
        <MessageStateContext.Provider value={state}>
            <MessageDispatchContext.Provider value={dispatch}>
                {state.ConfirmationMessage !== undefined ?
                    <OkDialog
                        title={state.ConfirmationMessage.title}
                        open
                        onClose={() => {
                            state.ConfirmationMessage?.onClose && state.ConfirmationMessage.onClose();
                            dispatch({ type: MessageDispatchTypes.SetConfirmation,
                                payload: undefined });
                        }}
                        className={css.message}
                    >
                        {state.ConfirmationMessage.message}
                    </OkDialog>
                    : null
                }
                {state.YesNoMessage !== undefined ?
                    <YesNoDialog
                        title={state.YesNoMessage.title}
                        open
                        onYes={() => {
                            state.YesNoMessage?.onYes && state.YesNoMessage.onYes();
                            dispatch({ type: MessageDispatchTypes.SetYesNo,
                                payload: undefined });
                        }}
                        onNo={() => {
                            state.YesNoMessage?.onNo && state.YesNoMessage.onNo();
                            dispatch({ type: MessageDispatchTypes.SetYesNo,
                                payload: undefined });
                        }}
                        yesText={state.YesNoMessage.yesText}
                        noText={state.YesNoMessage.noText}
                        className={css.message}
                    >
                        {state.YesNoMessage.message}
                    </YesNoDialog>
                    : null
                }
                {props.children}
            </MessageDispatchContext.Provider>
        </MessageStateContext.Provider>
    );
};

export const useMessageState = () => {
    const context = useContext(MessageStateContext);
    if (!context) {
        throw new Error('No Message Context');
    }

    return context;
};

export const useMessageDispatch = () => {
    const context = useContext(MessageDispatchContext);
    if (context === undefined) {
        throw new Error('No Message Dispatch Context');
    }

    return context;
};

export const useSetMessage = () => {
    const messageDispatch = useMessageDispatch();

    return useCallback((message: ConfirmationMessage | undefined) => {
        messageDispatch({ type: MessageDispatchTypes.SetConfirmation,
            payload: message });
    }, [messageDispatch]);
};

export const useSetYesNoMessage = () => {
    const messageDispatch = useMessageDispatch();

    return useCallback((message: YesNoMessage | undefined) => {
        messageDispatch({ type: MessageDispatchTypes.SetYesNo,
            payload: message });
    }, [messageDispatch]);
};